import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Package, Headphones, Settings, FileText, PlayCircle, AlertCircle, PackageOpen, ClipboardList, Truck, CheckSquare, StickyNote, ShoppingCart, BarChart2, ListOrdered, CalendarCheck, Tag, Database, Layout, Upload, Link2, Image, Layers, RefreshCw, Search, Globe, BookOpen, Boxes } from 'lucide-react';
import MainNavigation from '@/components/MainNavigation';
import { DashboardSkeleton } from '@/components/skeletons';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';


// Alle tilgjengelige hurtigtilganger
export const DASHBOARD_SHORTCUTS = [
  // ── Ordrebehandling ──
  { key: 'ordrebekreftelse', label: 'Ordrebekreftelse', desc: 'Registrer bestilte varer fra leverandør', path: '/ordrebekreftelse', icon: ClipboardList, adminOnly: false },
  { key: 'varer-i-bestilling', label: 'Varer i bestilling', desc: 'Oversikt over bestilte varer (på rest øverst)', path: '/varer-i-bestilling', icon: Truck, adminOnly: false },
  { key: 'varemottak', label: 'Varemottak (PDF)', desc: 'Last opp pakkseddel når varer ankommer', path: '/varemottak', icon: PackageOpen, adminOnly: false },
  { key: 'kundebestilling', label: 'Kundebestilling', desc: 'Registrer og følg opp kundebestillinger', path: '/kundebestilling', icon: ShoppingCart, adminOnly: false },
  { key: 'salg', label: 'Salg', desc: 'Salgsoversikt og kampanjer', path: '/salg', icon: Tag, adminOnly: false },
  { key: 'orders', label: 'Ordreliste', desc: 'Oversikt over alle ordrer', path: '/orders', icon: ListOrdered, adminOnly: false },

  // ── Lagerstyring ──
  { key: 'lagerstyring', label: 'Lagerstyring', desc: 'Beholdning · På rest · Utsolgt', path: '/lagerstyring', icon: BarChart2, adminOnly: false },
  { key: 'periodisk-varetelling', label: 'Periodisk varetelling', desc: 'Ukesplan · Tell per sted · Synk til WooCommerce', path: '/periodisk-varetelling', icon: CalendarCheck, adminOnly: false },
  { key: 'kasse-samspill', label: 'Kasse-samspill (PCK)', desc: 'Importer salgsstatistikk fra PCK og oppdater lager', path: '/kasse-samspill', icon: ShoppingCart, adminOnly: false },
  { key: 'varetelling', label: 'Varetelling', desc: 'Manuell varetelling', path: '/varetelling', icon: CheckSquare, adminOnly: false },

  // ── Varebehandling ──
  { key: 'produktmaster', label: 'Produktmaster', desc: 'Sentral database for alle produkter', path: '/produktmaster', icon: Database, adminOnly: false },
  { key: 'product-kobling', label: 'Produktkobling', desc: 'Koble Master-produkter til WooCommerce', path: '/product-kobling', icon: Link2, adminOnly: true },
  { key: 'publiseringsplan', label: 'Publiseringsplan', desc: 'Planlegg publisering av nye produkter', path: '/publiseringsplan', icon: CalendarCheck, adminOnly: true },
  { key: 'import', label: 'Importer Varer', desc: 'Import fra web, Excel eller tilbehør', path: '/import', icon: Upload, adminOnly: true },
  { key: 'importer-varer', label: 'Gjennomgå import', desc: 'Gjennomgå og importer skrapede produkter', path: '/importer-varer', icon: Package, adminOnly: true },
  { key: 'garnpakker', label: 'Garnpakker', desc: 'Importer og administrer garnpakker', path: '/garnpakker', icon: Boxes, adminOnly: true },
  { key: 'data-integrity', label: 'Data Integritet', desc: 'Sjekk koblingen mellom oppskrifter og garnpakker', path: '/data-integrity', icon: CheckSquare, adminOnly: true },
  { key: 'oppskriftsbibliotek', label: 'Oppskriftsbibliotek', desc: 'Søk, print og administrer alle oppskrifter på tvers av systemer', path: '/oppskriftsbibliotek', icon: BookOpen, adminOnly: false },
  { key: 'oppskrift-innsjekk', label: 'Oppskrift-innsjekk', desc: 'Dra inn PDF-er, sjekk status mot Woo og importer batch-vis', path: '/oppskrift-innsjekk', icon: Upload, adminOnly: true },

  // ── Synkronisering ──
  { key: 'nyheter', label: 'Nyheter Dashboard', desc: 'Se nye og utgåtte produkter fra leverandører', path: '/nyheter', icon: AlertCircle, adminOnly: false },
  { key: 'woo-sync', label: 'WooCommerce Sync', desc: 'Synkroniser produkter med nettbutikken', path: '/woo-sync', icon: RefreshCw, adminOnly: true },
  { key: 'matching', label: 'Universal Matching', desc: 'Match produkter mellom systemer', path: '/matching', icon: Search, adminOnly: true },
  { key: 'conflicts', label: 'Konflikter', desc: 'Løs datakonflikter mellom systemer', path: '/conflicts', icon: AlertCircle, adminOnly: true },
  { key: 'templates', label: 'Synkroniseringsmaler', desc: 'Administrer URLer for nattlig synkronisering', path: '/synkroniseringsmaler', icon: FileText, adminOnly: true },
  { key: 'sync-dashboard', label: 'Sync Dashboard', desc: 'Oversikt over synkroniseringsstatus', path: '/sync-dashboard', icon: RefreshCw, adminOnly: true },

  // ── Innstillinger ──
  { key: 'leverandor-oppsett', label: 'Leverandør-oppsett', desc: 'Administrer leverandører, merker og designere', path: '/leverandor-oppsett', icon: Settings, adminOnly: false },
  { key: 'leverandor-bestilling', label: 'Bestilling & Service', desc: 'Bestillingsmåte, kontakter og service per leverandør', path: '/leverandor-oppsett?tab=bestilling', icon: Headphones, adminOnly: false },
  { key: 'excel-import', label: 'Excel Import', desc: 'Importer garn fra Sandnes, Rauma, osv.', path: '/excel-import', icon: FileText, adminOnly: true },
  { key: 'mapping-status', label: 'Mapping Status', desc: 'Se umappede produkter og manglende felt', path: '/missing-fields', icon: AlertCircle, adminOnly: true },
  { key: 'attr-setup', label: 'Attributt Mapping', desc: 'Koble attributter til garnpakker', path: '/attributes', icon: Layers, adminOnly: true },
  { key: 'supplier-images', label: 'Leverandørbilder', desc: 'Last opp og administrer leverandørbilder', path: '/supplier-images', icon: Image, adminOnly: true },
  { key: 'settings', label: 'Innstillinger', desc: 'Generelle appinnstillinger', path: '/settings', icon: Settings, adminOnly: true },
  { key: 'setup', label: 'WooCommerce Oppsett', desc: 'Konfigurer tilkobling til din nettbutikk', path: '/setup', icon: Settings, adminOnly: true },
  { key: 'agent-notater', label: 'Agent Notater', desc: 'Notater og viktig informasjon for agenter', path: '/agent-notater', icon: StickyNote, adminOnly: true },
  { key: 'profile', label: 'Min Profil', desc: 'Administrer brukere og leverandørfiler', path: '/profile', icon: Layout, adminOnly: false },

  // ── Excel Import per leverandør ──
  { key: 'sandnes-import', label: 'Sandnes Import', desc: 'Importer EAN-fil fra Sandnes Garn', path: '/sandnes-import', icon: FileText, adminOnly: true },
  { key: 'rauma-import', label: 'Rauma Import', desc: 'Importer EAN-fil fra Rauma Garn', path: '/rauma-import', icon: FileText, adminOnly: true },
  { key: 'viking-import', label: 'Viking Import', desc: 'Importer EAN-fil fra Viking Garn', path: '/viking-import', icon: FileText, adminOnly: true },
  { key: 'hoy-import', label: 'Høy Import', desc: 'Importer EAN-fil fra Høy Garn', path: '/hoy-import', icon: FileText, adminOnly: true },
  { key: 'hillesvag-import', label: 'Hillesvåg Import', desc: 'Importer EAN-fil fra Hillesvåg', path: '/hillesvag-import', icon: FileText, adminOnly: true },
  { key: 'filcolana-import', label: 'Filcolana Import', desc: 'Importer EAN-fil fra Filcolana', path: '/filcolana-import', icon: FileText, adminOnly: true },
  { key: 'isager-import', label: 'Isager Import', desc: 'Importer EAN-fil fra Isager', path: '/isager-import', icon: FileText, adminOnly: true },
  { key: 'permin-import', label: 'Permin Import', desc: 'Importer EAN-fil fra Permin', path: '/permin-import', icon: FileText, adminOnly: true },
  { key: 'villy-import', label: 'Villy Import', desc: 'Importer EAN-fil fra Villy', path: '/villy-import', icon: FileText, adminOnly: true },
  { key: 'kortoggodt-import', label: 'Kort og Godt Import', desc: 'Importer EAN-fil fra Kort og Godt', path: '/kortoggodt-import', icon: FileText, adminOnly: true },
];

export const DEFAULT_SHORTCUTS = [
  'periodisk-varetelling', 'kundebestilling', 'ordrebekreftelse',
  'varer-i-bestilling', 'varemottak', 'leverandor-oppsett',
  'leverandor-bestilling', 'nyheter',
];

// Snarveier som alltid skal vises, selv for brukere som lagret oppsettet sitt før
// snarveien fantes (ansatte fant ikke fram til Bestilling & Service).
const ALWAYS_INCLUDED_SHORTCUTS = ['leverandor-bestilling'];

const Dashboard = () => {
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recentJobs, setRecentJobs] = useState([]);
  const [supplierStats, setSupplierStats] = useState(null);
  const [msAlerts, setMsAlerts] = useState(null);
  const [garnpakkeNewsCount, setGarnpakkeNewsCount] = useState(0);
  const [garnpakkeBySupplier, setGarnpakkeBySupplier] = useState([]);
  const [varetellingStatus, setVaretellingStatus] = useState(null);
  const [huskeliste, setHuskeliste] = useState(null);
  const [epostVarsler, setEpostVarsler] = useState(null);
  const [conflictCount, setConflictCount] = useState(0);
  const [shortcuts, setShortcuts] = useState(null); // null = ikke lastet ennå
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    checkConfiguration();
    fetchRecentJobs();
    fetchSupplierStats();
    fetchMsAlerts();
    fetchGarnpakkeAlerts();
    fetchVaretellingStatus();
    fetchHuskeliste();
    fetchEpostVarsler();
    fetchConflicts();
    fetchDashboardPreferences();
  }, []);

  const fetchConflicts = async () => {
    try {
      const r = await api.get('/conflicts/count');
      setConflictCount(r.data?.unresolved || 0);
    } catch {}
  };

  const fetchHuskeliste = async () => {
    try {
      const r = await api.get('/leverandor-komm/huskeliste/count');
      if (r.data?.open_total > 0) setHuskeliste(r.data);
    } catch {}
  };

  const fetchEpostVarsler = async () => {
    try {
      const r = await api.get('/email-inbox/notifications');
      if ((r.data?.count || 0) > 0) setEpostVarsler(r.data);
    } catch {}
  };

  const fetchMsAlerts = async () => {
    try {
      const r = await api.get('/leverandorservice/dashboard-alerts');
      if (r.data.modellstrikk_total > 0) setMsAlerts(r.data);
    } catch {}
  };

  const fetchVaretellingStatus = async () => {
    try {
      const r = await api.get('/varetelling/dashboard-status');
      if (r.data?.uncounted > 0) setVaretellingStatus(r.data);
    } catch {}
  };

  const fetchGarnpakkeAlerts = async () => {
    try {
      const r = await api.get('/supplier-monitor/garnpakke-news');
      // Bare leverandører hvor katalogen har noe nytt siden sist sjekk
      const news = (r.data?.news || []).filter(n => n.has_new);
      setGarnpakkeNewsCount(news.length);
      setGarnpakkeBySupplier(news.map(n => ({
        name: n.supplier_name || n.supplier || 'Ukjent',
        count: n.new_count || 1,
      })));
    } catch {}
  };

  const fetchDashboardPreferences = async () => {
    try {
      const r = await api.get('/auth/dashboard-preferences');
      if (r.data.success && r.data.shortcuts) {
        setShortcuts([...new Set([...r.data.shortcuts, ...ALWAYS_INCLUDED_SHORTCUTS])]);
      } else {
        setShortcuts(DEFAULT_SHORTCUTS);
      }
    } catch {
      setShortcuts(DEFAULT_SHORTCUTS);
    }
  };

  const checkConfiguration = async () => {
    try {
      const response = await api.get('/woocommerce/config');
      const isConfigured = response.data?.configured === true;
      setConfigured(isConfigured);
    } catch (error) {
      console.error('Feil ved sjekk av konfigurasjon:', error);
      setConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentJobs = async () => {
    try {
      const response = await api.get('/sync/jobs');
      setRecentJobs(response.data.slice(0, 5));
    } catch (error) {
      console.error('Feil ved henting av jobber:', error);
    }
  };
  
  const fetchSupplierStats = async () => {
    try {
      const response = await api.get('/supplier-monitor/dashboard-stats');
      setSupplierStats(response.data);
    } catch (error) {
      console.error('Feil ved henting av leverandør-statistikk:', error);
    }
  };

  if (loading) {
    return (
      <>
        <MainNavigation />
        <div className="min-h-screen bg-slate-50 px-4 sm:px-6 py-6">
          <div className="max-w-[1400px] mx-auto animate-in fade-in duration-300">
            <DashboardSkeleton />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <MainNavigation />
      <div className="min-h-screen bg-slate-50 px-4 sm:px-6 py-6">
        <div className="max-w-[1400px] mx-auto animate-in fade-in duration-300">
          <div className="mb-6">
            <h1 className="text-3xl font-semibold text-slate-900 mb-1" data-testid="dashboard-title">
              Oversikt
            </h1>
            <p className="text-slate-500">
              Velkommen til RetailMaster
            </p>
          </div>

        {!configured && (
          <Card className="mb-4 border-l-4 border-l-amber-400" data-testid="config-warning">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <AlertCircle size={24} className="text-amber-600 shrink-0" />
                <div>
                  <h3 className="text-amber-800 font-semibold text-sm">WooCommerce ikke konfigurert</h3>
                  <p className="text-amber-700 text-sm mt-0.5">Du må konfigurere WooCommerce API-tilgang før du kan bruke appen.</p>
                  <Link to="/setup">
                    <Button size="sm" className="mt-3 bg-teal-600 hover:bg-teal-700" data-testid="goto-setup-btn">
                      Gå til oppsett
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Varsels-grid: produktoppdateringer (inkl. garnpakker) + modellstrikk + varetelling.
            Kortene vises kun hvis relevante. Layout: 3 kolonner på desktop, stablet på mobil. */}
        {(() => {
          const totalProductPending = (supplierStats?.total_pending || 0) + (garnpakkeNewsCount || 0);
          const showProducts = totalProductPending > 0;
          const showModellstrikk = !!(msAlerts && msAlerts.modellstrikk_total > 0);
          const showVaretelling = !!(varetellingStatus && varetellingStatus.uncounted > 0);
          // Vis rolle-varsel for admin når noen kvaliteter har butikk/lager-status
          const rs = varetellingStatus?.role_summary;
          const showVaretellingRole = !!(isAdmin && rs && (rs.both_done_not_sent > 0 || rs.butikk_done > 0 || rs.lager_done > 0));
          const showHuskeliste = !!(huskeliste && huskeliste.open_total > 0);
          const showEpost = !!(epostVarsler && epostVarsler.count > 0);
          if (!showProducts && !showModellstrikk && !showVaretelling && !showVaretellingRole && !showHuskeliste && !showEpost) return null;

          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {/* E-post varsler — auto-behandlede/manuelle e-poster */}
              {showEpost && (() => {
                const created = (epostVarsler.notifications || []).filter(n => n.auto_processed?.status === 'created');
                const garnpakke = (epostVarsler.notifications || []).filter(n => n.auto_processed?.status === 'garnpakke');
                const manual = (epostVarsler.notifications || []).filter(n => !['created', 'garnpakke'].includes(n.auto_processed?.status));
                const unmatched = created.reduce((s, n) => s + (n.auto_processed?.unmatched || 0), 0);
                const nyeGarnpakker = garnpakke.reduce((s, n) => s + (n.auto_processed?.new_count || 0), 0);
                const parts = [];
                if (created.length) parts.push(`${created.length} ordrebekreftelse${created.length > 1 ? 'r' : ''} behandlet`);
                if (unmatched) parts.push(`${unmatched} vare(r) ikke matchet`);
                if (nyeGarnpakker) parts.push(`${nyeGarnpakker} garnpakker mangler`);
                if (manual.length) parts.push(`${manual.length} må behandles manuelt`);
                const text = parts.length ? parts.join(', ') + '.' : `${epostVarsler.count} e-postvarsel.`;
                return (
                  <Card className="border-l-4 border-l-amber-500" data-testid="epost-varsel-alert">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <AlertCircle size={18} className="text-amber-600 shrink-0" />
                        <h3 className="text-slate-800 font-semibold text-sm leading-tight">
                          {epostVarsler.count} e-postvarsel
                        </h3>
                      </div>
                      <p className="text-slate-600 text-xs mb-3 leading-snug">{text}</p>
                      <Link to="/epost-innboks">
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="goto-epost-btn">
                          Åpne e-post innboks
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Huskeliste — handlingspunkter fra leverandørbeskjeder */}
              {showHuskeliste && (() => {
                const bt = huskeliste.by_type || {};
                const parts = [];
                if (bt.prisjustering) parts.push(`${bt.prisjustering} prisjustering${bt.prisjustering > 1 ? 'er' : ''}`);
                if (bt.utgaende_vare) parts.push(`${bt.utgaende_vare} utgående`);
                if (bt.importer_garnpakke) parts.push(`${bt.importer_garnpakke} garnpakke${bt.importer_garnpakke > 1 ? 'r' : ''}`);
                if (bt.tilbud_utgaar) parts.push(`${bt.tilbud_utgaar} tilbud`);
                const text = parts.length ? parts.join(', ') + '.' : `${huskeliste.open_total} punkter å følge opp.`;
                return (
                  <Card className="border-l-4 border-l-rose-500" data-testid="huskeliste-alert">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <CheckSquare size={18} className="text-rose-600 shrink-0" />
                        <h3 className="text-slate-800 font-semibold text-sm leading-tight">
                          {huskeliste.open_total} på huskelisten
                        </h3>
                      </div>
                      <p className="text-slate-600 text-xs mb-3 leading-snug">{text}</p>
                      <Link to="/huskeliste">
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="goto-huskeliste-btn">
                          Åpne huskeliste
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Produktoppdateringer (nye + utgåtte + garnpakker) */}
              {showProducts && (() => {
                const parts = [];
                if (supplierStats?.new_products) parts.push(`${supplierStats.new_products} nye produkter`);
                if (supplierStats?.discontinued_products) parts.push(`${supplierStats.discontinued_products} utgåtte varer`);
                if (garnpakkeNewsCount) parts.push(`${garnpakkeNewsCount} ${garnpakkeNewsCount === 1 ? 'leverandør' : 'leverandører'} med nye garnpakker`);
                const text = parts.length === 0
                  ? ''
                  : parts.length === 1
                    ? parts[0] + '.'
                    : parts.slice(0, -1).join(', ') + ' og ' + parts[parts.length - 1] + '.';
                return (
                  <Card className="border-l-4 border-l-emerald-500" data-testid="product-updates-alert">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <AlertCircle size={18} className="text-emerald-600 shrink-0" />
                        <h3 className="text-slate-800 font-semibold text-sm leading-tight">
                          Nye produktoppdateringer
                        </h3>
                      </div>
                      <p className="text-slate-600 text-xs mb-3 leading-snug">{text}</p>
                      <Link to="/nyheter">
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="goto-nyheter-btn">
                          Se nyheter
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Modellstrikk klare for innsending */}
              {showModellstrikk && (
                <Card className="border-l-4 border-l-blue-500" data-testid="modellstrikk-alert">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <AlertCircle size={18} className="text-blue-600 shrink-0" />
                      <h3 className="text-slate-800 font-semibold text-sm leading-tight">
                        {msAlerts.modellstrikk_total} modellstrikk klar{msAlerts.modellstrikk_total > 1 ? 'e' : ''}
                      </h3>
                    </div>
                    <p className="text-slate-600 text-xs mb-3 leading-snug">
                      Fra {msAlerts.modellstrikk_suppliers.map(s => `${s.name} (${s.count})`).join(', ')}.
                    </p>
                    <Link to="/leverandor-oppsett">
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="goto-modellstrikk-btn">
                        Bestilling & Service
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}

              {/* Datakonflikter fra nattlig Woo-sync */}
              {conflictCount > 0 && (
                <Card className="border-l-4 border-l-amber-500" data-testid="conflicts-alert">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <AlertCircle size={18} className="text-amber-600 shrink-0" />
                      <h3 className="text-slate-800 font-semibold text-sm leading-tight">
                        {conflictCount} datakonflikt{conflictCount > 1 ? 'er' : ''} å løse
                      </h3>
                    </div>
                    <p className="text-slate-600 text-xs mb-3 leading-snug">
                      Avvik mellom master og Woo fra nattlig synk. Gjennomgå og velg riktig verdi.
                    </p>
                    <Link to="/conflicts">
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="goto-conflicts-btn">
                        Åpne konflikter
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}

              {/* Varetelling — ukomplett denne uken. Rødt hvis >50% utelte, ellers samme stil som produktoppdateringer. */}
              {showVaretelling && (() => {
                const total = varetellingStatus.total || 0;
                const uncounted = varetellingStatus.uncounted || 0;
                const isCritical = total > 0 && uncounted / total > 0.5;
                const borderClr = isCritical ? 'border-l-red-500' : 'border-l-emerald-500';
                const iconClr = isCritical ? 'text-red-600' : 'text-emerald-600';
                return (
                  <Card className={`border-l-4 ${borderClr}`} data-testid="varetelling-alert">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <AlertCircle size={18} className={`${iconClr} shrink-0`} />
                        <h3 className="text-slate-800 font-semibold text-sm leading-tight">
                          Ufullstendig varetelling
                        </h3>
                      </div>
                      <p className="text-slate-600 text-xs mb-3 leading-snug">
                        {uncounted} av {total} varer er ikke talt ennå
                        {varetellingStatus.week_key ? ` (${varetellingStatus.week_key})` : ''}.
                      </p>
                      <Link to="/periodisk-varetelling">
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="goto-varetelling-btn">
                          Gå til varetelling
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Rolle-status for admin — vis når butikk/lager har meldt ferdig */}
              {isAdmin && varetellingStatus?.role_summary && (() => {
                const rs = varetellingStatus.role_summary;
                const ready = rs.both_done_not_sent || 0;
                const butikkDone = rs.butikk_done || 0;
                const lagerDone = rs.lager_done || 0;
                const total = rs.qualities_total || 0;
                // Vis kun hvis det finnes noe å rapportere
                if (!ready && !butikkDone && !lagerDone) return null;
                const borderClr = ready > 0 ? 'border-l-teal-500' : 'border-l-blue-500';
                const iconClr = ready > 0 ? 'text-teal-600' : 'text-blue-600';
                return (
                  <Card className={`border-l-4 ${borderClr}`} data-testid="varetelling-role-alert">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <AlertCircle size={18} className={`${iconClr} shrink-0`} />
                        <h3 className="text-slate-800 font-semibold text-sm leading-tight">
                          {ready > 0 ? `${ready} kvalitet${ready > 1 ? 'er' : ''} klar til regnskap` : 'Telling pågår'}
                        </h3>
                      </div>
                      <p className="text-slate-600 text-xs mb-3 leading-snug">
                        🏪 Butikk: {butikkDone}/{total} ferdig · 📦 Lager: {lagerDone}/{total} ferdig
                        {ready > 0 ? ` · ${ready} venter på admin` : ''}
                      </p>
                      <Link to="/periodisk-varetelling">
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="goto-varetelling-role-btn">
                          {ready > 0 ? 'Send til regnskap' : 'Se status'}
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
          );
        })()}

        {/* Fordeling alerts — fjernet (fordeling-prosessen er satt på vent) */}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-6">
          {(() => {
            const activeKeys = shortcuts || (isAdmin ? DASHBOARD_SHORTCUTS.map(s => s.key) : DEFAULT_SHORTCUTS);
            return activeKeys
              .map(key => DASHBOARD_SHORTCUTS.find(sc => sc.key === key))
              .filter(sc => sc && (!sc.adminOnly || isAdmin))
              .map(sc => {
                const Icon = sc.icon;
                return (
                  <Card
                    key={sc.key}
                    className="group hover:border-teal-300 hover:shadow-md transition-all duration-150 cursor-pointer"
                    data-testid={`quick-action-${sc.key}`}
                    onClick={() => window.location.href = sc.path}
                  >
                    <CardContent className="p-4">
                      <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center mb-3 text-teal-600 group-hover:bg-teal-100 transition-colors">
                        <Icon size={20} />
                      </div>
                      <h3 className="text-slate-900 font-semibold text-sm mb-1">{sc.label}</h3>
                      <p className="text-slate-500 mb-3 text-xs leading-relaxed">{sc.desc}</p>
                      <Link to={sc.path} onClick={e => e.stopPropagation()}>
                        <Button variant="outline" size="sm" className="w-full text-sm hover:bg-teal-50 hover:text-teal-700 hover:border-teal-300">
                          Åpne
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              });
          })()}
        </div>

        {recentJobs.length > 0 && isAdmin && (
          <Card className="mt-6" data-testid="recent-jobs-section">
            <CardContent className="p-5">
              <h2 className="text-slate-900 font-semibold text-base mb-4 flex items-center gap-2">
                <PlayCircle size={20} />
                Nylige jobber
              </h2>
              <div className="flex flex-col gap-2">
                {recentJobs.map((job, index) => (
                  <div
                    key={job.id}
                    className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-lg border border-slate-100"
                    data-testid={`job-item-${index}`}
                  >
                    <div>
                      <h4 className="text-slate-800 font-medium text-sm">{job.template_type}</h4>
                      <p className="text-xs text-slate-500">
                        {new Date(job.created_at).toLocaleString('nb-NO')}
                      </p>
                    </div>
                    <Badge
                      data-testid={`job-status-${index}`}
                      variant={job.status === 'completed' ? 'default' : job.status === 'running' ? 'secondary' : 'destructive'}
                      className={job.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : ''}
                    >
                      {job.status === 'completed' ? 'Fullført' : job.status === 'running' ? 'Kjører' : 'Feilet'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    </>
  );
};

export default Dashboard;
